<template>
  <div
    class="predictive-search-bar"
    :class="{[$class('loading')]: loading}"
  >
    <ais-search-box>
      <template #default="{ refine, currentRefinement }">
        <form
          class="predictive-search-bar__form"
          role="search"
          @submit.prevent="onSubmit(refine)"
        >
          <button
            :aria-label="$string('search.search')"
            role="img"
            class="predictive-search-bar__icon"
            @click="onSubmit(refine)"
          >
            <icon-search />
          </button>

          <input
            ref="searchInput"
            type="search"
            placeholder="Search"
            class="predictive-search-bar__input text-body-s"
            autocomplete="off"
            js-predictive-search="input"
            :value="getValue(currentRefinement)"
            @focus="searchFocussed"
            @keyup.enter.prevent
            @keydown.enter="onSubmit(refine)"
            @input="updateSearchQuery($event, refine)"
          >

          <button
            :aria-label="$string('search.search')"
            type="button"
            class="predictive-search-bar__clear"
            @click="clearSearch"
          >
            <icon-close />
          </button>
        </form>
      </template>
    </ais-search-box>
  </div>
</template>

<script>

/**
 * Vue: Predictive search bar (predictive-search-bar)
 * -----------------------------------------------------------------------------
 * The predictive search bar in the site header.
 *
 * @param {Boolean} loading - Show loading template.
 * @param {Boolean} dynamicReload - Handles whether the component only updates on submit.
 *
 */
import { mapState } from 'vuex'
import { AisSearchBox } from 'vue-instantsearch/vue3/es'
import { debounce } from '~/helpers/general'

import IconSearch from '~icons/misc/search.svg'
import IconClose from '~icons/misc/close.svg'


export default {
  name: 'PredictiveSearchBar',

  components: {
    AisSearchBox,
    IconClose,
    IconSearch,
  },

  props: {
    dynamicReload: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },

  emits: [
    'search-active',
    'search-focussed',
  ],

  data() {
    return {
      input: '',
      touched: false,
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      settings: (state) => state.index.settings,
    }),
  },

  created(){
    this.debouncedRefineResults = debounce(this.refineResults, this.settings.throttle)
  },

  methods: {

    /**
     * Allows the search query to be updated from the parent.
     * @param {Event} event - Input event.
     * @param {Function} refine - Algolia refine function.
     */
    updateSearchQuery(event, refine) {
      const value = event?.currentTarget?.value || ''
      this.input = value
      this.touched = true

      if(this.dynamicReload) {
        return
      }

      this.debouncedRefineResults(refine, value)
    },

    /**
     * Allows the search query to be updated from the parent.
     * @param {Function} refine - Algolia refine function.
     * @param {String} query - Input field value.
     */
    refineResults(refine, query) {
      this.$emit('search-active', query)
      refine(query)
    },

    /**
     * Handles emitting an event when the input is newly focussed.
     * @param {Object} val - Focus event.
     */
    searchFocussed(val) {
      const focus = document.activeElement === val.target
      this.$emit('search-focussed', focus)
    },

    getValue(currentRefinement) {

      /**
       * Before interaction with search box, set input from currentRefinement
       * and fire event search-active
       */
      if(!this.touched) {
        this.input = currentRefinement
        this.$emit('search-active', this.input)
      }

      return this.input
    },

    /**
     * Clears and focuses the input, hides results from the previous query and clear button.
     */
    clearSearch() {
      this.touched = true
      this.input = ''
    },

    /**
     * Redirects browser to the results page.
     * @param {Function} refine - Algolia refine function.
     */
    onSubmit(refine) {
      if (this.dynamicReload) {
        this.$emit('search-active', this.input)
        refine(this.input)
        return
      }

      window.location = this.getSearchUrl(this.input)
    },

    /**
     * Get url for search page including Root route.
     */
    getSearchUrl(query) {
      const searchRoute = window.cnvs.routes.search || '/search'

      const url = `${searchRoute}?query=${query}`
      return url
    },
  },
}
</script>

<style lang="scss">
  @import './predictive-search-bar';
</style>
